// http://www.petecorey.com/blog/2019/08/19/animating-a-canvas-with-react-hooks/

import "./LiveUnit.css";
import { Grid, GridSize, Box } from "@mui/material";
import { useContext, useEffect, useState, useRef } from "react";
import  LiveUnitHeader  from "../../../components/device/LiveUnitHeader";
import { Device } from "../../../interfaces/Device";
import { Waveform } from "../../../interfaces/vitals/Waveform";
import { UserContext } from "../../../context/UserContext";
import "../../Waveforms/Waveforms.css";
import { GetSocketName } from "../../../utils/functions/GetSocketName";
import { LiveUnitWaveforms } from "./LiveUnitWaveforms";
import {SelectParameters} from "../../../components/device/SelectParameters";
import * as CONSTANTS from "../../../utils/constants/constants";
import UpdateVitals from "../../../utils/functions/UpdateVitals";
import { PatientVitals } from "../../../interfaces/vitals/PatientVitals";
import { AlarmsContext } from "../../../context/AlarmsContext";
import { isBrowser } from "react-device-detect";
import { isMobile } from "react-device-detect";
import { useExternalScript } from "../../../hooks/useExternalScript";
import { toast } from "react-toastify";
import { Event } from "../../WaveMonitor";
import { LiveUnitParameters } from "./LiveUnitParameters";

// Utility function to convert base64 string to number array
const base64ToNumberArray = (base64?: string): number[] => {
	if (!base64) {
		return []
	}
	const binaryString = atob(base64);
	const len = binaryString.length;
	const bytes = new Uint8Array(len);
	for (let i = 0; i < len; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return Array.from(bytes);
};

const SOCKET_OPEN = 1;



export interface LiveUnitProps {
	device: Device;
	availableWaveforms: string[];
	availableParameters: string[];
	width?: GridSize;
	type?: string;
	waveTitle?: boolean;
	wavePosition?: string;
	header: boolean;
	minHeight: number;
	parameterBox: boolean;
	useLink?: boolean;
	showWaveform?: boolean;
	hideCamera?: boolean;
	addEvent?: (event: Event) => void;
	alarmProp?: any;
	selectedIntervention?: string;
	triggerRefresh?: (intervention_id: string) => void;  // Accept the triggerRefresh function as a prop
	setSelectedIntervention?: (value: string) => void;
	liveIntervention?: string;
	showSettings?: boolean;
	showEkgRecording?: boolean;
	showPatientInfo?: boolean;
	showConnectionStatus?: boolean;
	showDeviceName?: boolean;
	showChartIcon?: boolean;
	showDeviceInfo?: boolean;
	updateActivityStatus?: (device_id: string, timestamp: string, monitorId?: string) => void;
	isVisible: boolean;
	dynamicSwimlanes?: boolean;

}

export interface CanvasDims {
	width: number;
	height: number;
	maxHeight?: number;
	minHeight?: number;
}

interface LiveDataPacket {
	parameters: PatientVitals;
	patientInfo: PatientInfo;
	intervention_id: string;
}

export interface PatientInfo {
	first_name?: string;
	last_name?: string;
	age?: string;
	gender?: string;
	id?: string;
	patient_id?: string;
	hospital?: string;
	bed_no?: string;
	category?: string;  // Adult, Child, Neonate, Unknown
}


const useWindowDimensions = () => {
	const [windowDimensions, setWindowDimensions] = useState({
	  windowWidth: window.innerWidth,
	  windowHeight: window.innerHeight,
	});
  
	useEffect(() => {
	  const handleResize = () => {
		setWindowDimensions({
			windowWidth: window.innerWidth,
			windowHeight: window.innerHeight,
		});
	  };
  
	  window.addEventListener('resize', handleResize);
  
	  // Cleanup event listener on component unmount
	  return () => {
		window.removeEventListener('resize', handleResize);
	  };
	}, []);
  
	return windowDimensions;
  };


// ----------------------------------------------------
//
// LiveUnit
//
// This component displays live data for a monitor.
//
// ----------------------------------------------------
export const LiveUnit = ({
	device,
	availableWaveforms,
	availableParameters,
	parameterBox,
	useLink,
	header,
	minHeight,
	width = 6,
	type = "livemonitor",
	showWaveform = false,
	hideCamera = false,
	addEvent,
	alarmProp,
	selectedIntervention,
	liveIntervention,
	triggerRefresh,
	showSettings = true,
	showChartIcon = true,
	showEkgRecording = true,
	showPatientInfo = true,
	showConnectionStatus = true,
	showDeviceName = true,
	showDeviceInfo = true,
	wavePosition = "middle",
	updateActivityStatus = undefined,
	isVisible = true,
	dynamicSwimlanes = true

}: LiveUnitProps) => {
	//console.log ("+++ +++ LiveUnit renders", device, availableParameters, availableWaveforms);
	//console.log ("--- --- device_def:", JSON.parse (device.device_def))
	const [socket, setSocket] = useState<any | null>(null);
	const userCtx = useContext(UserContext);
	const [myDims, setMyDims] = useState<CanvasDims>({
		height: minHeight,
		width: 0,
	});

	const { windowWidth,  } = useWindowDimensions();
    const [isMounted, setIsMounted] = useState(false);

	const timer = useRef<any>();
	const componentRef = useRef<HTMLDivElement>(null);
	const [liveData, setLiveData] = useState<LiveDataPacket>({
		parameters: {},
		patientInfo: {},
		intervention_id: ""
	});
	const savedParams = JSON.parse(
		// @ts-ignore
		localStorage.getItem(type + "-" + device?.device_id)
	);
	
	// Clean up saved params to enforce mutual exclusion rules (only for LiveDashboard)
	const cleanSavedParams = (params: string[] | null): string[] => {
		if (!params || params.length === 0) return [];
		
		// Only apply mutual exclusion for LiveDashboard (type === "livemonitor")
		if (type !== "livemonitor") {
			return params;
		}
		
		const ibpChannels = ['IBP1', 'IBP2', 'IBP3', 'IBP4'];
		const ecgLeads = ['ECG_L1', 'ECG_L2', 'ECG_L3'];
		
		let cleaned = [...params];
		
		// If multiple IBP channels are saved, only keep the first one
		const savedIBPs = params.filter(w => ibpChannels.includes(w));
		if (savedIBPs.length > 1) {
			cleaned = cleaned.filter(w => !ibpChannels.includes(w) || w === savedIBPs[0]);
		}
		
		// If multiple ECG leads are saved, only keep the first one
		const savedECGs = params.filter(w => ecgLeads.includes(w));
		if (savedECGs.length > 1) {
			cleaned = cleaned.filter(w => !ecgLeads.includes(w) || w === savedECGs[0]);
		}
		
		return cleaned;
	};
	
	const last_datagram_ts = useRef(0);
	const [parmConnectStatus, setParmConnectStatus] = useState("disconnected");
	const connectStatus = useRef(false);
	const [selectableWaveforms, setSelectableWaveforms] = useState<string[]>([]);
	const [selectedWaveforms, setSelectedWaveforms] = useState<string[]>(cleanSavedParams(savedParams));
	const [parmLength, setParmLength] = useState(0)
	const [isVideoOn, setIsVideoOn] = useState(false);
	const alarmsCtx = useContext(AlarmsContext);
	const [showLive, setShowLive] = useState(true);
	const [eqpAlarmsEnabled, setEquimentAlarmEnabled]= useState (false);
	const [headerDevice, setHeaderDevice] = useState <Device | undefined> (undefined)
	const [headerPatientInfo, setHeaderPatientInfo]= useState ({})
	const waveformQueue = useRef<Waveform[][]>([]); // Shared queue reference
    const [queueSize, setQueueSize] = useState(0); // State to track queue size
	const [selectedBoxParameters, setSelectedBoxParameters] = useState<string[]>([]);

	const start_monitor_connectivity_check = () => {
		timer.current = setTimeout(check_monitor_timeout, 3000, this);
		//console.log ("Start connectivity check ", device.device_id)
	};

	useEffect (() => {
		setParmLength (selectedWaveforms.length)
	}, [selectedWaveforms])

	useEffect (() => {
		//console.log ("change selectableWaveforms:", selectableWaveforms)
		if (selectableWaveforms.length === 0) {
			return
		}
		if (selectedWaveforms.length === 0) {
			// Only apply mutual exclusion rules for LiveDashboard (type === "livemonitor")
			if (type === "livemonitor") {
				const ibpChannels = ['IBP1', 'IBP2', 'IBP3', 'IBP4'];
				const ecgLeads = ['ECG_L1', 'ECG_L2', 'ECG_L3'];
				
				let initialSelection = [...selectableWaveforms];
				
				// If multiple IBP channels are available, only select the first one
				const availableIBPs = selectableWaveforms.filter(w => ibpChannels.includes(w));
				if (availableIBPs.length > 1) {
					// Remove all IBPs except the first one
					initialSelection = initialSelection.filter(w => !ibpChannels.includes(w) || w === availableIBPs[0]);
				}
				
				// If multiple ECG leads are available, only select the first one
				const availableECGs = selectableWaveforms.filter(w => ecgLeads.includes(w));
				if (availableECGs.length > 1) {
					// Remove all ECG leads except the first one
					initialSelection = initialSelection.filter(w => !ecgLeads.includes(w) || w === availableECGs[0]);
				}
				
				setSelectedWaveforms(initialSelection);
			} else {
				// For WaveMonitor, select all available waveforms
				setSelectedWaveforms(selectableWaveforms);
			}
		} else {
			// Add any newly available waveforms to the selection
			const newWaveforms = selectableWaveforms.filter(w => !selectedWaveforms.includes(w));
			if (newWaveforms.length > 0) {
				// Only apply mutual exclusion rules for LiveDashboard (type === "livemonitor")
				let allowedNewWaveforms = newWaveforms;
				
				if (type === "livemonitor") {
					const ibpChannels = ['IBP1', 'IBP2', 'IBP3', 'IBP4'];
					const ecgLeads = ['ECG_L1', 'ECG_L2', 'ECG_L3'];
					
					// Check if we already have an IBP or ECG lead selected
					const currentIBP = selectedWaveforms.find(w => ibpChannels.includes(w));
					const currentECG = selectedWaveforms.find(w => ecgLeads.includes(w));
					
					// Filter out new waveforms that would conflict with mutual exclusion rules
					allowedNewWaveforms = newWaveforms.filter(w => {
						// Don't add new IBP channels if one is already selected
						if (currentIBP && ibpChannels.includes(w)) {
							return false;
						}
						// Don't add new ECG leads if one is already selected
						if (currentECG && ecgLeads.includes(w)) {
							return false;
						}
						return true;
					});
				}
				
				if (allowedNewWaveforms.length > 0) {
					const updatedSelection = [...selectedWaveforms, ...allowedNewWaveforms];
					setSelectedWaveforms(updatedSelection);
					// Update localStorage
					localStorage.setItem(type + "-" + device?.device_id, JSON.stringify(updatedSelection));
				}
			}
		}
        // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectableWaveforms])
	const check_monitor_timeout = () => {
		if (timer.current === 0) {
			return;
		}
		setTimeout(check_monitor_timeout, 3000, this);

		let time_now = new Date().getTime();
		let diff = time_now - last_datagram_ts.current;
		//console.log ("check timeout:", device.device_id, last_datagram_ts.current, diff)

		if (diff > CONSTANTS.CONNECTIVITY_TIMEOUT) {
			if (connectStatus.current) {
				//console.log ("Disconnected", device.device_id)
				setParmConnectStatus("disconnected");
				connectStatus.current = false;
			}
		}
	};

	const strobe_connectivity = () => {
		//console.log ("Strobe:", device.device_id)
		if (!connectStatus.current) {
			//console.log ("Connected:", device.device_id)
			setParmConnectStatus("connected");
			connectStatus.current = true;
		}
		last_datagram_ts.current = new Date().getTime();
	};

	useEffect(() => {
		setIsMounted (true)

		if (componentRef && componentRef.current) {

			// Calculate height based on number of selected waveforms when using dynamic swimlanes
			const FIXED_SWIMLANE_HEIGHT = 57.142857142857146;
			const calculatedHeight = dynamicSwimlanes && selectedWaveforms.length > 0 
				? selectedWaveforms.length * FIXED_SWIMLANE_HEIGHT 
				: 0;

			if (isMounted)
				setMyDims({
					height: calculatedHeight > 0 ? calculatedHeight : minHeight,
					width: componentRef?.current?.clientWidth - (isVideoOn ? 204 : 0),
				});
		}

		setSelectableWaveforms(availableWaveforms);

	}, [ isMounted,
		isVideoOn,
		componentRef?.current?.clientWidth,
		minHeight,
		parameterBox,
		availableWaveforms,
		windowWidth,
		dynamicSwimlanes,
		selectedWaveforms.length
	]);
	//console.log ("Draw waveforms:", myDims)
	const handleVisibilityChange = () => {
		//console.log ("handleVisibilityChange")
		const user = userCtx?.user;
		if (!user || !user.organization || !user.user_id) {
			return;
		}
		if (document.visibilityState === "hidden") {
			//console.log ("Doc is hidden - stop streaming")
			stopStreaming(
				socket,
				device.device_id,
				user.organization,
				user.user_id
			);
		} else {
			//console.log ("Doc is visible - start streaming")
			startStreaming(
				socket,
				device.device_id,
				user.organization,
				user.user_id
			);
		}
	}; // handleVisibillityChange

	//
	// Keep track of whether the document is visible or not. If it's not visible then
	// temporarily stop streaming data since it won't be seen anyway.
	//
	useEffect(() => {
		if (socket == null) {
			return;
		}
		//console.log ("Adding eventlistener for visibilitychange")
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socket, device.device_id, userCtx?.user]);

	useEffect(() => {
		let isMounted = true;
		if (socket == null) {
			return;
		}
		if (isMounted) {
			//console.log ("CONNECTING SOCKET")
			connectWebSocket(socket, onSocketOpen, onSocketMessage);
			start_monitor_connectivity_check();
		}
		return () => {
			isMounted = false;
			closeWebSocket(socket, onSocketOpen, onSocketMessage);
			clearTimeout(timer.current);
			timer.current = 0;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socket]);

	//
	// Set up websocket to the device. This websocket lives as long as the LiveUnit component is active.
	//
	useEffect(() => {
		let isMounted = true;
		let socket_name =
			"vicu-socket/" +
			userCtx?.user?.organization +
			"/" +
			device?.device_id;
		let webSocket: WebSocket;
		if (isMounted) {
			//console.log ("CREATE NEW WEBSOCKET: ", GetSocketName(socket_name))
			webSocket = new WebSocket!(GetSocketName(socket_name));
			setSocket(webSocket);
		}

		// Save the equipment alarm enable state
		const almDef = JSON.parse (device.alarm_profile);
		setEquimentAlarmEnabled (almDef.eqp_alarms)

		if (!headerDevice) {
			setHeaderDevice (device)
			//console.log ("NEW HEADERDEVICE")
		}
		
		//console.log ("Alarm enbled:", almDef, almDef.eqp_alarms)
		// Runs when LiveUnit component is unmounted
		return () => {
			isMounted = false;
			closeWebSocket(webSocket, onSocketOpen, onSocketMessage);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [device, userCtx?.user?.organization]);


	const onSocketOpen = () => {
		//console.log ("onSocketOpen:", userCtx?.user)
		const user = userCtx?.user;
		if (!user || !user.organization || !user.user_id) {
			return;
		}
		startStreaming(
			socket,
			device.device_id,
			user.organization,
			user.user_id
		);
	}; // onSocketOpen

	const comparePatientInfo = (p1: PatientInfo, p2: PatientInfo) => {
		if (p1.age !== p2.age) return false;
		if (p1.bed_no !== p2.bed_no) return false;
		if (p1.category !== p2.category) return false;
		if (p1.first_name !== p2.first_name) return false;
		if (p1.gender !== p2.gender) return false;
		if (p1.hospital !== p2.hospital) return false;
		if (p1.patient_id !== p2.patient_id) return false;
		if (p1.id !== p2.id) return false;
		if (p1.last_name !== p2.last_name) return false;

		return true;
	}


	const onSocketMessage = (message: any) => {
		//@ts-ignore
		let data = JSON.parse(message.data);
		
		// Log vital signs data packets for WaveMonitor
		if (type === "livewaveforms") {
			/*
			console.log("📊 WaveMonitor Data Packet:", {
				device_id: data.message?.device_id,
				timestamp: data.message?.timestamp,
				parameters: data.message?.parameters ? Object.keys(data.message.parameters) : [],
				waveforms: data.message?.waveforms ? data.message.waveforms.map((w: any) => w.name) : [],
				full_data: data
			});
			*/
		}
		
		//console.log ("onSocketMessage:", data.message.device_id, data.message.transaction_id, data)
		const timestamp_str = data.message.timestamp;
		const device_id     = data.message.device_id;

		//console.log ("Device datagram:", data.message.device_id, "Timestamp:", timestamp_str)
		switch (data?.opcode) {
			case "device_datagram": {
				strobe_connectivity();
				const event = message;
				const myVitals: PatientVitals = UpdateVitals(
					event,
					null,
					null,
					alarmsCtx?.setAlarms,
					null
				);

				delete myVitals?.IBP2;
				//delete myVitals?.Temp;

				//console.log ("My vitals:", myVitals)

				//console.log (device.device_id, "-------------- Device datagram:", Date())
				const msg = data.message;
				const patientInfo = msg.patient;
				const waves = msg.waveforms;
				const parms = msg.parameters;
				const waveArray: Waveform[] = [];
				const intervention_id: string = msg.intervention_id;


				var parameters = null;
				if (waves) {
					waves.forEach((w: Waveform) => {
						//console.log ("Process waves:", w)
						const ww: Waveform = {
							digital_max: w.digital_max,
							digital_min: w.digital_min,
							error_code: w.error_code,
							sampleValidity: base64ToNumberArray(w.validity),
							n_samples: w.n_samples,
							name: w.name,
							physical_dim: w.physical_dim,
							physical_max: w.physical_max,
							physical_min: w.physical_min,
							samplerate: w.samplerate,
							samples: w.samples,
							text: w.text,
							valid: w.valid ? 1 : 0,
							validity: w.validity,
						};
						waveArray.push(ww);
						if (ww.name === "ECG_L2") {
							//console.log (timestamp_str, "--> ECG_L2:", w.samples.slice(0, 10))
						}

					}); // for all waves
					} // if waves

				if (parameters) {
					parameters = parms;
				}

				//console.log ("onSoecketMessage waveArray:", waveArray)
				waveformQueue.current.push(waveArray);
				setQueueSize(waveformQueue.current.length); // Update the queue size

				const liveData = {
					patientInfo: patientInfo,
					parameters: myVitals,
					intervention_id: intervention_id
				};
				//console.log ("/ / / / / / / set liveData:", Date(), liveData)

				setLiveData(liveData);

				break;
			} // device datagram

			case "alarm-event": {
				const msg = data.message;
				const alms = msg.alarms;
				const device_id = msg.device_id;
				for (var i = 0; i < alms.length; i++) {
					alms[i]["device_id"] = device.nickname
						? device.nickname
						: device_id;
				}
				if (device.enable_alarms) {
					//console.log ("Alarm:", device_id, alms)
					alarmsCtx?.setAlarms(alms);
					if (addEvent) {
						//var alm_e: Event = {
						//	timestamp: new Date (msg["timestamp"]),
						//	eventType: "alarm",
						//	description: data.message,
						//	eventData: ""
						//}
						// Disable alarms for now.
						// addEvent(alm_e);
					}
				} else {
					//console.log ("Squelch alarm:", alms)
				}

				break;
			}

			case "device_trend_datagram": {
				break;
			}

			case "event": {
				const msg = data.message;
				const devicename = device.nickname
					? device.nickname
					: msg.device_id;
				//console.log("Event msg:", msg);
				var dark = false;
				const eventType = msg["event_type"];
				const intervention_id = msg["intervention_id"]
				// console.log("Event type:", eventType);

				switch (eventType) {
					case "msg": {
						if (addEvent) {
							var event_m: Event = {
								timestamp: new Date(msg["timestamp"]),
								eventType: "msg",
								description: msg["event_data"],
								eventData: "",
								intervention_id: intervention_id
							};
						addEvent(event_m);
						}
						break;
					}

					case "restingecg": {
						//toast ( devicename + " : Resting ECG received", {
						//	position: "bottom-center",
						//	theme: dark ? "dark" : "light"
						//})

						if (addEvent) {
							const evData: string = JSON.stringify({
								type: "pdf",
								data: msg["event_data"],
							});
							var descStr = "Resting ECG received";
							var ecg_e: Event = {
								timestamp: new Date(msg["timestamp"]),
								eventType: "ecg",
								description: descStr,
								eventData: evData,
								intervention_id: intervention_id
							};
							addEvent(ecg_e);
							toast(devicename + " : " + descStr, {
								position: "bottom-center",
								theme: dark ? "dark" : "light",
							});
						}

						break;
					} // restingecg

					case "defib": {
						const msgData = JSON.parse(msg["event_data"]);
						var datastr: string = "";
						if ("Description" in msgData) {
							datastr = msgData["Description"];
							if (datastr === "DEFI: Discharge") {
								dark = true;
							}
						}
						if ("Energy" in msgData) {
							datastr =
								"Energy: " + msgData["Energy"] + " Joules";
						}

						if (datastr !== "" && dark === true) {
							toast(devicename + " : " + datastr, {
								position: "bottom-center",
								theme: dark ? "dark" : "light",
							});
						}
						if (addEvent) {
							var event_e: Event = {
								timestamp: new Date(msg["timestamp"]),
								eventType: "event",
								description: datastr,
								eventData: "",
								intervention_id: intervention_id
							};
							addEvent(event_e);
						}

						break;
					} // defib
					case "image": {
						if (addEvent) {
							var event_i: Event = {
								timestamp: new Date(msg["timestamp"]),
								eventType: "image",
								description: msg["fileName"],
								eventData: JSON.stringify ({
									"unique_id" : msg["unique_id"],
									"thumbnail" : msg["thumbnail"]
								}),
								intervention_id: intervention_id
							};
							addEvent(event_i);
						}
						break;
					}
		
				} // switch eventType

				break;
			} // case "event"

			default: {
				console.log("Unknown message:", data);
			}
		} // switch
		if (updateActivityStatus) {
			if (timestamp_str) {
				updateActivityStatus (device_id, timestamp_str);
			}
		}
			
	}; // onSocketMessage

	useEffect(() => {
		//console.log ("patient info is", liveData.patientInfo)
		if (!liveData.patientInfo || Object.entries(liveData.patientInfo).length === 0) {
			return
		}
		if (!comparePatientInfo (liveData.patientInfo, headerPatientInfo)) {
			setHeaderPatientInfo (liveData.patientInfo)
			//console.log ("New header patient info")
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [liveData.patientInfo])

	// If the intervention_id changes, then we need to refresh the data. Also we need to decide to show the live data or not.
	// liveData should be true if the current intervention_id is the same as the liveData.intervention_id
	// If he currentInterventionId is not the last entry in the interventions array, then set showLive to false

	useEffect (() => {
		if (liveData.intervention_id !== selectedIntervention) {
			if (triggerRefresh) {
				triggerRefresh (liveData.intervention_id)
			}
		}
		//console.log ("Live intervention:", liveData.intervention_id, "Selected:", selectedIntervention)
		setShowLive (!selectedIntervention || (selectedIntervention === liveData.intervention_id))

	}, [liveData.intervention_id, selectedIntervention, triggerRefresh])


	const state = useExternalScript("ivideon/iv-standalone-web-sdk.js");

	const startCamera = () => {
		if (isVideoOn) {
			setIsVideoOn(false);
			return;
		}
		setIsVideoOn(true);
		if (state === "ready") {
			// @ts-ignore
			_ivideon.sdk
				.init({
					rootUrl: "ivideon/",
					l10nOptions: {
						availableLanguages: ["de", "en", "fr"],
						language: "en",
					},
				})
				.then(
					function (sdk: any) {
						sdk.configureWithCloudApiAuthResponse({
							api_host: "openapi-alpha.ivideon.com",
							access_token: device?.camera_token,
						});

						var camera = sdk.createCamera({
							id: device?.camera_id,
							cameraName: "Pet Cam",
							imageWidth: 800,
							imageHeight: "123px",
							soundEnabled: false,
						});

						var player = sdk.createPlayer({
							container: "#ivideon_stream_" + device.device_id,
							camera: camera,
						        sizeCalculationMethod : sdk.playerSize.FILL_BY_WIDTH,
							"timelineEnabled" : false,
							"playPauseButtonEnabled" : false,
							"goToLiveButtonEnabled" : false,
							"volumeControlEnabled" : false,
							"qualityControlEnabled" : false,
							"speedControlEnabled" : false,
							"previewEnabled" : false,
							"previewFromCameraEnabled" : false,
						        layout: "expanded"

						});

						player.playLive({ quality: 2 });
					},
					function (error: any) {
						console.error(error);
					}
				)
				.catch((error: any) => {
					console.error(error);
				});
		}
	};

	const paramBoxWidthRef = useRef("0");
	useEffect(() => {
		var r = document.querySelector("#liveUnitHeader");
		if (r) {
			var rs = getComputedStyle(r);
			const w: number = parseInt(
				rs.getPropertyValue("width").replace("px", "")
			);
			const parameterBoxWidth = w - 205;
			paramBoxWidthRef.current = parameterBoxWidth.toString() + "px";
		}
	}, []);

	useEffect(() => {
		if (parameterBox) {
			// For LiveDashboard (type is undefined or not "livewaveforms"), always show all available parameters
			// For WaveMonitor (type is "livewaveforms"), dynamically show parameters based on selected waveforms
			if (type !== "livewaveforms") {
				setSelectedBoxParameters(availableParameters);
			} else {
			// Use a Set to ensure uniqueness
			const parmlistSet = new Set<string>();
	
			/*
				* Add parameter according to the selected waveforms:
				* ECG_L1, ECG_L2, ECG_L3 and/or ECG_Defi: HR
				* SpO2: SpO2
				* IBP1/2/3/4: IBP1/2/3/4 (whichever is selected)
				* EtCO2: EtCO2 and RR
				*/
			selectedWaveforms.forEach((wave) => {
				switch (wave) {
					case "ECG_L1":
					case "ECG_L2":
					case "ECG_L3":
					case "ECG_Defi":
						parmlistSet.add("HR");
						break;
					case "SpO2":
						parmlistSet.add("HR");
						parmlistSet.add("SpO2");
						break;
					case "IBP1":
						parmlistSet.add("IBP1");
						break;
					case "IBP2":
						parmlistSet.add("IBP2");
						break;
					case "IBP3":
						parmlistSet.add("IBP3");
						break;
					case "IBP4":
						parmlistSet.add("IBP4");
						break;
					case "EtCO2":
					case "Resp":
						parmlistSet.add("EtCO2");
						parmlistSet.add("RR");
						break;
				}
			});				// Convert the Set back to an array
				const parmlist = Array.from(parmlistSet);
				setSelectedBoxParameters(parmlist);
			}
		}
	}, [selectedWaveforms, parameterBox, type, availableParameters]);
	
	



	// --------------------------------------------------------------------------------------------------
	//
	// The display html
	//
	//console.log ("liveData:", liveData)
	//console.log ("intervention_id:", intervention_id, "Current:", currentInterventionId)
	//console.log ("selectable waves:", selectableWaveforms, "Selected:", selectedWaveforms)
	if (!headerDevice) {
		return <></>
	}
	if (!isVisible) {
		return <></>
	}
	//console.log ("major: selectedwaveforms:", selectedWaveforms)
	return (
			<Grid
				container
				item
				xs={12}
				sm={width ?? 6}
				md={width ?? 6}
				className="liveUnit"
			>

				<Grid style={{ width: '100%', border: '0.5px solid grey', borderRadius: '5px 5px 0 0'}} item
>
					{header && (
						<LiveUnitHeader
							key={headerDevice?.device_id}
							device={headerDevice}
							patientInfo={headerPatientInfo}
							useLink={useLink}
							connectStatus={parmConnectStatus}
							nParmsSelected={parmLength}
							selectParams={
								<SelectParameters
									deviceId={headerDevice?.device_id}
									selectableParameters={selectableWaveforms}
									selectedParameters={selectedWaveforms}
									setSelectedParameters={setSelectedWaveforms}
									maxParameters={availableWaveforms.length}
									type={type}
									deviceModel={device.device_model}
									enforceMutualExclusion={type === "livemonitor"}
								/>
							}
							fullWidth={width === 12}
							isVideoOn={isVideoOn}
							startCamera={hideCamera ? undefined : startCamera}
							alarmProp={alarmProp}
							showSettings={showSettings}
							showChartIcon={showChartIcon}
							showEkgRecording={showEkgRecording}
							showPatientInfo={showPatientInfo}
							showConnectionStatus={showConnectionStatus}
							showDeviceName={showDeviceName}
							showDeviceInfo={showDeviceInfo}
						/>					
					)}
					<Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', height: dynamicSwimlanes ? 'auto' : '123px' }}>
						{/* Waveform box */}
						{(isBrowser || showWaveform) && (
							<Box ref={componentRef} className="waveformBox" sx={{ flex: 1, minWidth: 0, height: '100%' }}>
								<LiveUnitWaveforms 
									canvasDims={myDims}
									selectedWaves={selectedWaveforms}
									waveformQueue={waveformQueue}
									queueSize={queueSize} // Pass queue size as a prop
									showLiveWaveforms={ showLive }
									labelpos = {wavePosition}
									deviceModel={device.device_model}
									dynamicSwimlanes={dynamicSwimlanes}
								/>

							</Box>
						)}
						{/* Parameter box web/mobile when video is off */}
						{parameterBox &&
							!isVideoOn &&
							(isMobile ? (
								<Box
									className="parameterBox"
									width={"100%"}
								>
									<LiveUnitParameters
										availableParms={availableParameters}
										parms={liveData.parameters}
										connectStatus={parmConnectStatus}
										eqpAlarmsEnabled={eqpAlarmsEnabled}
									/>
								</Box>
							) : (
								<Box
									className="parameterBox"
									sx={{ 
										flexShrink: 0, 
										height: dynamicSwimlanes ? 'auto' : '123px',
										padding: dynamicSwimlanes ? '5px 5px 5px 8px' : '0px',
										boxSizing: 'border-box'
									}}
								>
									<LiveUnitParameters
										availableParms={selectedBoxParameters}
										parms={liveData.parameters}
										connectStatus={parmConnectStatus}
										eqpAlarmsEnabled={eqpAlarmsEnabled}
									/>
								</Box>
							))}
						{/* Parameter box on mobile when video is on */}
						{parameterBox && isVideoOn && isMobile && (
							<Box
								className="parameterBox"
								width={paramBoxWidthRef.current}
							>
								<LiveUnitParameters
									availableParms={availableParameters}
									parms={liveData.parameters}
									connectStatus={parmConnectStatus}
									eqpAlarmsEnabled={eqpAlarmsEnabled}
								/>
							</Box>
						)}
						{/* Video player */}
						{isVideoOn && state === "ready" && (
							<div
								className="myapp-player-container"
								id={"ivideon_stream_" + device.device_id}
								style={{
									width: "205px",
									height: "123px",
									backgroundColor: "black",
								}}
							></div>
						)}
					</Box>
				</Grid>
				{/* Parameter box on web when video is on */}
				{parameterBox && isVideoOn && !isMobile && (
					<LiveUnitParameters
						availableParms={availableParameters}
						parms={liveData.parameters}
						connectStatus={parmConnectStatus}
						isVideoOn={isVideoOn}
						eqpAlarmsEnabled={eqpAlarmsEnabled}
					/>
				)}
			</Grid>
	);

	// --------------------------------------------------------------------------------------------------
}; // LiveUnit


const startStreaming = (
	socket: any,
	device_id: string,
	organization: string,
	user_id: number
) => {
	if (socket !== null && socket.readyState === SOCKET_OPEN) {
		//console.log ("Start streaming:", socket)
		const stream = {
			type: "server-request",
			opcode: "stream_waveforms",
			organization: organization,
			device_id: device_id,
			user_id: user_id,
		};
		socket.send(JSON.stringify(stream));
	}
};

const stopStreaming = (
	socket: any,
	device_id: string,
	organization: string,
	user_id: number
) => {
	if (socket !== null && socket.readyState === SOCKET_OPEN) {
		//console.log ("Stop streaming:", socket)
		const stopStream = {
			type: "server-request",
			opcode: "stop_streaming",
			organization: organization,
			device_id: device_id,
			user_id: user_id,
		};
		socket.send(JSON.stringify(stopStream));
	}
};

const closeWebSocket = (
	webSocket: WebSocket,
	onOpenFunction: () => void,
	onMessageFunction: (message: any) => void
) => {
	webSocket.removeEventListener("open", onOpenFunction);
	webSocket.removeEventListener("message", onMessageFunction);
	if (webSocket !== null && webSocket.readyState === WebSocket.OPEN) {
		webSocket.close();
	}
};

const connectWebSocket = (
	socket: any,
	onOpenFunction: () => void,
	onMessageFunction: (message: any) => void
) => {
	//console.log("Connecting to WebSocket...", socket);
	socket.addEventListener("open", onOpenFunction);
	socket.addEventListener("message", onMessageFunction);
};
